import { Button, Input, Tag } from "antd";
import Downshift from "downshift";
import {
  any,
  append,
  concat,
  contains,
  defaultTo,
  dropLast,
  filter,
  find,
  findIndex,
  includes,
  is,
  isEmpty,
  isNil,
  last,
  lensPath,
  pathOr,
  pipe,
  pluck,
  prop,
  propEq,
  propOr,
  propSatisfies,
  reduce,
  reject,
  remove,
  set,
  uniqBy,
  view,
  without,
} from "ramda";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { areEqual, FixedSizeList as List } from "react-window";
import { SearchIcon } from "../../assets/svg";
import {
  debounceAsync,
  getLastElement,
  getValueByKey,
  isFunction,
} from "../../utilities/helpers";
import "./filterInput.scss";
import { DEBOUNCE_TIME } from "../../constants/constants";

const FREE_TEXT_HELP = "Enter Text to search";

const getAppliedSuggestions = (key, appliedFilters) =>
  pipe(
    filter(propEq("key", key)),
    pluck("value"),
    defaultTo([])
  )(appliedFilters);

const getFormattedAppliedFilters = (filters, appliedFilters) =>
  reduce(
    (acc, { key }) => {
      const appliedValues = pipe(
        filter(propEq("key", key)),
        pluck("value"),
        without(["any"])
      )(appliedFilters);

      return {
        ...acc,
        [`${key}`]:
          appliedValues.length > 1 ? appliedValues : last(appliedValues),
      };
    },
    {},
    filters
  );

const RenderInput = (props) => {
  const { InputProps, downshiftRef, inputFieldRef, ...other } = props;
  return (
    <Input
      onFocus={() => {
        if (downshiftRef) {
          downshiftRef.current.openMenu();
        }
      }}
      ref={inputFieldRef}
      className="chip-input-style"
      {...InputProps}
      {...other}
    />
  );
};

// const RenderSuggestion = ({
//   suggestion,
//   index,
//   itemProps,
//   highlightedIndex,
//   ...antdMenuProps
// }) => {
//   const isHighlighted = highlightedIndex === index;

//   return (
//     <Menu.Item
//       {...antdMenuProps}
//       {...itemProps}
//       key={`${index}`}
//       className={isHighlighted ? "selected" : ""}
//       title={suggestion.name}
//       {...(equals(suggestion.name, FREE_TEXT_HELP) && { disabled: true })}
//     >
//       {suggestion.name}
//     </Menu.Item>
//   );
// };

const RenderChip = ({ item, index, name, valueName, handleDelete }) => {
  const { key, value } = item;
  if (!key) return null;

  return (
    <div key={key} className="horizontal-div">
      <Tag key={key} closable={false} className="category-name">
        {name}
      </Tag>
      {(value || value === 0) && (
        <Tag
          key={value}
          closable
          onClose={() => handleDelete(index)}
          className="category-value"
        >
          <p className="tag-value">{valueName}</p>
        </Tag>
      )}
    </div>
  );
};

const removeNilBYValue = reject(
  (item) => isNil(item.value) || isEmpty(item.value)
);

const FilterInputSearch = ({
  activeTab,
  filters,
  onFilterChange,
  defaultAppliedFilters,
  disabled,
  placeholder = "Search or filter results...",
  handleResetData,
  getSuggestionsList,
  clearFilters,
  resetFlag,
  renderBulkAction,
  selectedRows = [],
  allSelected,
  showBulkSelect,
  count = 0,
  selectedCount = 0,
  unselectedRows = [],
  encodedFilters = {},
  selectedTab = "",
  onActionPerformed,
  clientSideFilter = false,
}) => {
  const isFetchingSuggestions = useRef(false);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [localFilters, setLocalFilters] = useState(filters);
  const [suggestions, setSuggestions] = useState([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState(suggestions);
  const [inputValue, setInputValue] = useState("");
  const [selectedItem] = useState({});
  const downshiftRef = useRef(null);
  const inputFieldRef = useRef(null);
  const getFilterName = (key) =>
    pipe(
      find(propEq("key", key)),
      defaultTo({}),
      prop("name")
    )(filters);
  useEffect(
    () => {
      setAppliedFilters([]);
    },
    [resetFlag]
  );
  const getFilterValueName = (key, value, name) =>
    pipe(
      find(propEq("key", key)),
      defaultTo({}),
      prop("values"),
      defaultTo([]),
      find(propEq("key", value)),
      defaultTo({}),
      propOr(value, "name"),
      defaultTo(name || value)
    )(localFilters);

  useEffect(
    () => {
      if (defaultAppliedFilters)
        setAppliedFilters(removeNilBYValue(defaultAppliedFilters));
    },
    [setAppliedFilters, defaultAppliedFilters]
  );

  // useEffect(() => {
  //   setAppliedFilters([]);
  // }, [filters]);

  useEffect(
    () => {
      if (filters) {
        setLocalFilters(filters);
      }
    },
    [filters, setLocalFilters]
  );

  useEffect(
    () => {
      if (!appliedFilters.length) return;

      const { value } = last(appliedFilters);

      if (!isEmpty(value)) applyFilters(appliedFilters);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [appliedFilters]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedGetSuggestions = useCallback(
    debounceAsync(getSuggestionsList, DEBOUNCE_TIME, false),
    [getSuggestionsList]
  );
  useEffect(
    () => {
      if (filters) {
        const appliedKeys = pluck("key", appliedFiltersRef.current);
        const visibleFilters = filters.filter(
          (filter) => !includes(filter.key, appliedKeys)
        );
        setSuggestions(filters);
        if (isEmpty(filteredSuggestions)) {
          setFilteredSuggestions(visibleFilters);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [setSuggestions, filters, setFilteredSuggestions]
  );

  const applyFilters = useCallback(
    (updatedAppliedFilters) => {
      downshiftRef.current && downshiftRef.current.closeMenu();
      inputFieldRef && inputFieldRef.current && inputFieldRef.current.blur();

      const formattedAppliedFilters = getFormattedAppliedFilters(
        filters,
        updatedAppliedFilters
      );
      onFilterChange(formattedAppliedFilters);
    },
    [filters, onFilterChange, downshiftRef, inputFieldRef]
  );

  const handleInputChange = useCallback(
    ({ target }) => {
      setInputValue(target.value);
    },
    [setInputValue]
  );

  const getSuggestions = useCallback(
    async (key) => {
      // case for getting categories suggestions
      if (isNil(key)) return localFilters;

      let updatedSuggestions = [];
      const filterObj = find(propEq("key", key), localFilters);
      if (!filterObj) return suggestions;

      const { dataSource, values, freeText } = filterObj;

      if (freeText) return [{ name: FREE_TEXT_HELP }];

      if (values && !isEmpty(values)) {
        updatedSuggestions = concat(updatedSuggestions, values);
      } else if (isFunction(dataSource) && !isFetchingSuggestions.current) {
        isFetchingSuggestions.current = true;
        const fetchedSuggestions = await dataSource();
        updatedSuggestions = is(Array, fetchedSuggestions)
          ? concat(updatedSuggestions, fetchedSuggestions)
          : updatedSuggestions;
        isFetchingSuggestions.current = false;
      }

      const appliedSuggestions = getAppliedSuggestions(key, appliedFilters);

      return pipe(
        reject(propSatisfies((x) => contains(x, appliedSuggestions), "key")),
        uniqBy(prop("key"))
      )(updatedSuggestions);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [localFilters, appliedFilters]
  );

  const handleRemoveFilter = useCallback(
    (index) => {
      setAppliedFilters((state) => {
        const updatedFiltered = remove(index, 1, state);
        const lastAppliedFilter = last(updatedFiltered);
        const lastItemKey =
          isEmpty(updatedFiltered) ||
          !lastAppliedFilter ||
          lastAppliedFilter.value ||
          lastAppliedFilter.value === 0
            ? null
            : lastAppliedFilter.key;

        getSuggestions(lastItemKey).then(setSuggestions);

        if (isEmpty(updatedFiltered)) applyFilters(updatedFiltered);

        return updatedFiltered;
      });
      downshiftRef.current.openMenu();
    },
    [
      setAppliedFilters,
      setSuggestions,
      applyFilters,
      getSuggestions,
      downshiftRef,
    ]
  );

  const handleRemoveAllFilter = useCallback(
    () => {
      setAppliedFilters([]);
      setSuggestions(filters);
      handleResetData();
      setInputValue("");
      if (applyFilters) applyFilters({});
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [setAppliedFilters, setSuggestions, handleResetData]
  );

  useEffect(
    () => {
      if (clearFilters !== undefined) handleRemoveAllFilter();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [clearFilters]
  );
  const handleChange = useCallback(
    async (itemKey) => {
      if (!itemKey && itemKey !== 0) return;
      let updatedAppliedFilters = [];
      const lastAppliedFilter = last(appliedFilters);
      if (
        isEmpty(appliedFilters) ||
        !lastAppliedFilter ||
        lastAppliedFilter.value ||
        lastAppliedFilter.value === 0
      ) {
        updatedAppliedFilters = append(
          { key: itemKey, value: "" },
          appliedFilters
        );
        const categoryIndex = findIndex(propEq("key", itemKey), localFilters);
        const categoryValuesLens = lensPath([categoryIndex, "values"]);

        const categoryIsFetchedLens = lensPath([categoryIndex, "isFetched"]);

        const values = view(categoryValuesLens)(localFilters);
        const isFetched = view(categoryIsFetchedLens)(localFilters);
        if (!getSuggestionsList && (!isFetched || isEmpty(values))) {
          const categorySuggestions = await getSuggestions(itemKey);
          // Update category values fetched, it will not re fetch once fetched
          setSuggestions(categorySuggestions);
          if (categorySuggestions && !isEmpty(categorySuggestions)) {
            setLocalFilters((state) => {
              return !isFetched
                ? pipe(
                    set(categoryValuesLens, categorySuggestions),
                    set(categoryIsFetchedLens, true)
                  )(state)
                : state;
            });
          }
        } else {
          setSuggestions(view(categoryValuesLens)(localFilters));
        }
      } else {
        const categorySuggestions = await getSuggestions();
        updatedAppliedFilters = [...appliedFilters];
        updatedAppliedFilters.pop();
        updatedAppliedFilters.push({
          key: lastAppliedFilter.key,
          value: itemKey,
        });
        setSuggestions(categorySuggestions);

        if (getSuggestionsList) {
          setFilteredSuggestions((ss) => {
            const suggestion = ss.find((x) => x.key === itemKey);
            if (suggestion) {
              updatedAppliedFilters.pop();
              updatedAppliedFilters.push({
                key: lastAppliedFilter.key,
                value: itemKey,
                name: suggestion.name,
              });
            }
            setAppliedFilters([...updatedAppliedFilters]);
            return ss;
          });
        }
      }
      setInputValue("");
      setAppliedFilters(updatedAppliedFilters);
      downshiftRef.current.openMenu();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      appliedFilters,
      setSuggestions,
      setInputValue,
      setLocalFilters,
      getSuggestions,
      localFilters,
    ]
  );

  const handleKeyDown = useCallback(
    (e) => {
      if (inputValue.length) return;
      let updated = false;
      if (e.keyCode !== 38) downshiftRef.current.closeMenu();
      switch (e.keyCode) {
        case 40:
          downshiftRef.current.openMenu();
          break;
        case 8:
          setAppliedFilters((state) => {
            if (!state.length) return state;

            const { key, value } = getLastElement(state);

            // if value exist then remove value and otherwise remove both category
            const lastElementIndex = state.length - 1;
            const lastCategoryValueLens = lensPath([lastElementIndex, "value"]);

            let updatedAppliedFilters = !isEmpty(value)
              ? set(lastCategoryValueLens, "", state)
              : dropLast(1, state);
            //  if backspace is pressed on filter value then get category values as suggestions otherwise get filters as suggestions
            if (isEmpty(value)) {
              getSuggestions(null).then((updatedSuggestions) => {
                setAppliedFilters((ap) => {
                  if (ap.length > 0) {
                    const { key: latestKey, value } = getLastElement(ap);
                    if (latestKey === key && (value === 0 || !value)) {
                      setSuggestions(updatedSuggestions);
                      const values = updatedSuggestions.filter(({ key }) => {
                        const filterItem = find(
                          propEq("key", key),
                          localFilters
                        );
                        const appliedSuggestions = getAppliedSuggestions(
                          key,
                          appliedFiltersRef.current
                        );
                        if (filterItem) return isEmpty(appliedSuggestions);

                        return true;
                      });
                      const filteredSuggestions = values.filter(
                        ({ name }) =>
                          name !== undefined &&
                          (isEmpty(inputValue) ||
                            name
                              .toLowerCase()
                              .includes(inputValue.toLocaleLowerCase()))
                      );
                      setFilteredSuggestions(filteredSuggestions);
                    }
                  }
                  updated = true;
                  appliedFiltersRef.current = ap;
                  return ap;
                });
              });
            } else {
              setSuggestions([]);
              setFilteredSuggestions([]);
              applyFilters(state.slice(0, -1));
            }
            const finalValue = updated
              ? appliedFiltersRef.current
              : updatedAppliedFilters;
            if (clientSideFilter) {
              applyFilters(finalValue);
            } else {
              if (isEmpty(finalValue)) applyFilters(finalValue);
            }

            return finalValue;
          });

          downshiftRef.current.openMenu();
          break;
        default:
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inputValue, localFilters, downshiftRef, setAppliedFilters, applyFilters]
  );

  const onPressEnter = useCallback(
    () => {
      if (!appliedFilters.length) return;

      const { key, value } = last(appliedFilters);

      const filterItem = find(propEq("key", key), localFilters);
      const { freeText } = filterItem ? filterItem : { freeText: false };

      if (freeText && !value) handleChange(inputValue);
    },
    [inputValue, localFilters, handleChange, appliedFilters]
  );

  const suggestionsRef = useRef(suggestions);
  suggestionsRef.current = suggestions;
  const appliedFiltersRef = useRef(appliedFilters);
  appliedFiltersRef.current = appliedFilters;
  useEffect(
    () => {
      let valuesSuggestions = false;
      let filtersEmpty = false;
      if (getSuggestionsList) {
        valuesSuggestions = !isEmpty(appliedFilters);
        if (valuesSuggestions) {
          const { value } = getLastElement(appliedFilters);
          valuesSuggestions = !value && value !== 0;
        } else {
          filtersEmpty = true;
        }
      } else if (!filtersEmpty) {
        const suggestions = suggestionsRef.current;
        valuesSuggestions =
          isEmpty(suggestions) ||
          any((suggestion) => {
            const item = find(propEq("key", suggestion.key), localFilters);
            return !item;
          }, suggestions);
      }
      if (!valuesSuggestions) {
        const values = localFilters.filter((filterItem) => {
          const appliedSuggestions = getAppliedSuggestions(
            filterItem.key,
            appliedFilters
          );
          if (filterItem) return isEmpty(appliedSuggestions);

          return true;
        });
        setFilteredSuggestions(
          values.filter(
            ({ name }) =>
              name !== undefined &&
              (isEmpty(inputValue) ||
                name.toLowerCase().includes(inputValue.toLocaleLowerCase()))
          )
        );
      } else {
        setFilteredSuggestions([]);
        if (getSuggestionsList) {
          const lastFilter = last(appliedFilters);
          if (lastFilter && !lastFilter.value && lastFilter.value !== 0) {
            debouncedGetSuggestions(
              lastFilter && lastFilter.key,
              inputValue || "",
              encodedFilters
            ).then((suggestions) => {
              const lastAppliedFilter = last(appliedFiltersRef.current);
              if (
                lastAppliedFilter &&
                lastFilter.key === lastAppliedFilter.key &&
                !lastAppliedFilter.value &&
                lastAppliedFilter.value !== 0
              )
                setFilteredSuggestions(suggestions);
            });
          }
        } else {
          setFilteredSuggestions(
            suggestions.filter(
              ({ name }) =>
                name !== undefined &&
                (isEmpty(inputValue) ||
                  name
                    .toString()
                    .toLowerCase()
                    .includes(inputValue.toLocaleLowerCase()))
            )
          );
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [inputValue, appliedFilters]
  );

  const domManipulation = () => {
    const inputWrapper = document.querySelectorAll(
      ".ant-input-group-wrapper.custom-filters .ant-input-wrapper.ant-input-group"
    );

    let firstNode = "";
    let secondNode = "";
    let thirdChild = "";

    inputWrapper.forEach((item, index) => {
      const inputContainer = document.createElement("section");
      inputContainer.className = "input-with-dropdown-container";

      firstNode = item?.children[0];
      secondNode = item?.children[1];
      thirdChild = item?.children[2];

      if (secondNode) {
        inputContainer.insertBefore(thirdChild, null);
        inputContainer.insertBefore(secondNode, null);
        firstNode.appendChild(inputContainer);
      }
    });
  };
  const showBulkAction = showBulkSelect && selectedRows.length > 0;
  useEffect(
    () => {
      if (!showBulkAction) domManipulation();
    },
    [activeTab, showBulkAction]
  );

  const allRowsSelected = selectedCount === count;
  const itemToString = useCallback((item) => {
    return pathOr(null, ["key"], item);
  }, []);

  const Row = memo(({ data, index, style }) => {
    const { items, highlightedIndex, getItemProps } = data;
    const item = getValueByKey(items, index);
    return (
      <li
        className={`filter-list-item ${
          highlightedIndex === index ? "selected" : ""
        }`}
        style={style}
        {...getItemProps({
          key: item.key,
          index,
          item: item.key,
        })}
      >
        {item.name}
      </li>
    );
  }, areEqual);

  const filterSuggestions = filteredSuggestions.filter(
    (data) => data?.name !== ""
  );

  return (
    <div
      className="filter-input-search"
      onClick={() => {
        inputFieldRef && inputFieldRef.current && inputFieldRef.current.focus();
      }}
    >
      {!showBulkAction && <SearchIcon />}
      <section className="filters-with-button">
        {!showBulkAction ? (
          <Downshift
            id="downshift-multiple"
            inputValue={inputValue}
            selectedItem={selectedItem}
            onChange={handleChange}
            ref={downshiftRef}
            itemToString={itemToString}
          >
            {({
              isOpen,
              getItemProps,
              getInputProps,
              highlightedIndex,
              getMenuProps,
              openMenu,
            }) => {
              return (
                <div className="filter-field-wrapper">
                  <span className="search-icon-container">
                    {/* <Icon type="search" /> */}
                  </span>
                  <RenderInput
                    className="custom-filters"
                    downshiftRef={downshiftRef}
                    width={120}
                    inputFieldRef={inputFieldRef}
                    InputProps={getInputProps({
                      onKeyDown: handleKeyDown,
                      onChange: handleInputChange,
                      onPressEnter,
                      onClick: () => openMenu(),
                      disabled,
                      // placeholder,
                      placeholder:
                        appliedFilters.length === 0 ? placeholder : null,
                      addonBefore: (
                        <>
                          {appliedFilters.map((item, index) => (
                            <RenderChip
                              name={getFilterName(item.key)}
                              valueName={
                                item.name ||
                                getFilterValueName(
                                  item.key,
                                  item.value,
                                  item.name
                                )
                              }
                              item={item}
                              index={index}
                              key={index}
                              handleDelete={handleRemoveFilter}
                              filters
                            />
                          ))}
                        </>
                      ),
                      addonAfter: (
                        <div className="filter-dropdown-trigger">
                          <ul
                            {...getMenuProps()}
                            className="filter-list-dropdown"
                          >
                            {isOpen ? (
                              <List
                                height={300}
                                itemCount={filterSuggestions.length}
                                itemData={{
                                  getItemProps,
                                  highlightedIndex,
                                  items: filterSuggestions.sort((a, b) => {
                                    if (isNaN(a.name) && isNaN(b.name))
                                      return a.name < b.name ? -1 : 1;
                                    return +a.name < +b.name ? -1 : 1;
                                  }),
                                }}
                                itemSize={35}
                                width={300}
                              >
                                {Row}
                              </List>
                            ) : null}
                          </ul>
                        </div>
                      ),
                    })}
                  />
                </div>
              );
            }}
          </Downshift>
        ) : (
          <>
            {/* {renderBulkAction(
              (allSelected && unselectedRows?.length) || !allSelected
                ? selectedRows
                : [],
              allSelected && unselectedRows?.length ? false : allSelected,
              selectedCount,
              unselectedRows,
              encodedFilters,
              selectedTab,
              onActionPerformed
            )} */}
            <div className="bulk-selected-info">
              <div className="bulk-selected-message">
                {allRowsSelected
                  ? `All ${count} record${count === 1 ? "" : "s"} selected`
                  : `${selectedCount} out of ${count} ${
                      appliedFilters.length > 0 ? "searched" : ""
                    } records selected`}
                {appliedFilters.length > 0 && (
                  <>
                    {" "}
                    from{" "}
                    <strong className="bulk-action-filters-wrapper">
                      {appliedFilters.map((item, index) => (
                        <>
                          {getFilterName(item.key)}:{" "}
                          <span className="bulk-action-filters">
                            {item.name ||
                              getFilterValueName(
                                item.key,
                                item.value,
                                item.name
                              )}
                          </span>
                          {index !== appliedFilters.length - 1 ? ", " : ""}
                        </>
                      ))}
                    </strong>
                    <Button
                      className="reset-filter-bulk"
                      type="link"
                      onClick={handleRemoveAllFilter}
                    >
                      Clear All Filters
                    </Button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
        {appliedFilters.length > 0 && !showBulkAction ? (
          <Button
            className="reset-filter"
            type="link"
            onClick={handleRemoveAllFilter}
          >
            Clear All Filters
          </Button>
        ) : (
          ""
        )}
      </section>
    </div>
  );
};

export default FilterInputSearch;
